import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import BlockIcon from "@mui/icons-material/Block";
import AppDialog from "../../shared/ui/AppDialog";
import { deactivateCompany } from "../../services/admin/companies";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { AdminCompany, REASON_MAX, REASON_MIN } from "./types";

export type DeactivateCompanyDialogProps = {
  company: AdminCompany | null;
  onClose: () => void;
  onDone: (message: string) => void;
};

const IMPACT_TAIL = "médicos deixam de poder pedir antecipação por esta empresa.";

// Aviso de impacto com o número real de pacotes abertos (openPackagesCount da listagem do Admin).
export const impactMessage = (openPackagesCount?: number) => {
  if (typeof openPackagesCount !== "number" || !Number.isFinite(openPackagesCount)) {
    return `Os pacotes abertos desta empresa continuam guardados e saem da visão padrão; ${IMPACT_TAIL}`;
  }
  if (openPackagesCount <= 0) return `Nenhum pacote aberto nesta empresa; ${IMPACT_TAIL}`;
  if (openPackagesCount === 1) return `1 pacote aberto continua guardado e sai da visão padrão; ${IMPACT_TAIL}`;
  return `${openPackagesCount} pacotes abertos continuam guardados e saem da visão padrão; ${IMPACT_TAIL}`;
};

export default function DeactivateCompanyDialog({ company, onClose, onDone }: DeactivateCompanyDialogProps) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Outra empresa (ou reabertura): começa limpo.
  useEffect(() => {
    setReason("");
    setError(null);
  }, [company?.id]);

  const trimmed = reason.trim();
  const valid = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;
  const managers = Number(company?.managersCount) || 0;

  const submit = async () => {
    if (!company || !valid) return;
    setSaving(true);
    setError(null);
    try {
      await deactivateCompany(company.id, trimmed);
      setSaving(false);
      onDone(`Empresa “${company.name}” desativada.`);
    } catch (e) {
      setSaving(false);
      // Outra pessoa desativou antes: o objetivo foi atingido, só atualiza a lista.
      if (getApiErrorCode(e) === "COMPANY_ALREADY_INACTIVE") {
        onDone(`A empresa “${company.name}” já estava inativa. A lista foi atualizada.`);
        return;
      }
      setError(getApiErrorMessage(e, "Não foi possível desativar a empresa."));
    }
  };

  return (
    <AppDialog
      open={Boolean(company)}
      onClose={onClose}
      busy={saving}
      size="sm"
      title="Desativar empresa"
      context={company ? `${company.name} · #${company.id} · CNPJ ${company.cnpj}` : undefined}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} sx={{ color: "text.primary" }}>
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={saving}
            disabled={!valid}
            startIcon={<BlockIcon />}
            onClick={submit}
          >
            Desativar empresa
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2}>
        <Alert severity="warning">{impactMessage(company?.openPackagesCount)}</Alert>
        <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13, color: "text.secondary", "& li": { mb: 0.5 } }}>
          <li>Some das listas e filtros (aparece com “Incluir inativas”).</li>
          <li>
            {managers === 1
              ? "1 gestor mantém o vínculo, marcado como inativa em Acessos."
              : `${managers} gestores mantêm o vínculo, marcado como inativa em Acessos.`}
          </li>
          <li>Dá para reativar depois; nada é apagado.</li>
        </Box>
        <TextField
          label="Motivo"
          required
          multiline
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex.: cadastro duplicado da integração Meu Plantão"
          inputProps={{ maxLength: REASON_MAX }}
          helperText={
            valid
              ? "Fica registrado na auditoria com seu nome e a data."
              : `Obrigatório (mínimo de ${REASON_MIN} caracteres). Fica registrado na auditoria.`
          }
        />
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </AppDialog>
  );
}
